import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from 'antd';
import type { FormInstance } from 'antd/es/form';

import { InputText } from '@/shared/ui/input';

import cls from './pattern-watchlist-form.module.css';

export interface FormValues {
    id?: number;
    name: string;
}

interface PatternWatchlistFormProps {
    data?: FormValues | undefined;
    form?: FormInstance<FormValues>;
    isLoading: boolean;
    isSuccess: boolean;
    onSubmit: (values: FormValues) => void;
}

export const PatternWatchlistForm = memo((props: PatternWatchlistFormProps) => {
    const {
        data,
        form: externalForm,
        isLoading = false,
        isSuccess = false,
        onSubmit,
    } = props;
    const { t } = useTranslation('pattern-watchlist');
    const [form] = Form.useForm<FormValues>();
    const formInstance = externalForm || form;

    const initialValues = useMemo(() => {
        if (!data) {
            return {};
        }

        return data;
    }, [data]);

    const onSave = useCallback(
        (formValues: FormValues) => {
            onSubmit({
                ...formValues,
                id: data?.id,
            } as FormValues);
        },
        [onSubmit, data?.id],
    );

    // Reset form fields when success
    useEffect(() => {
        if (isSuccess) {
            formInstance.resetFields();
        }
    }, [isSuccess, formInstance]);

    // Reset form fields when initial values change
    useEffect(() => {
        formInstance.resetFields();
    }, [formInstance, initialValues]);

    return (
        <Form
            autoComplete="off"
            className={cls.root}
            form={formInstance}
            initialValues={initialValues}
            layout="horizontal"
            onFinish={onSave}
        >
            <Form.Item
                label={t('name')}
                name="name"
                rules={[
                    {
                        required: true,
                        message: t('nameRequired'),
                    },
                ]}
            >
                <InputText
                    autoComplete="off"
                    disabled={isLoading}
                    isBlock
                    placeholder={t('namePlaceholder')}
                    size="large"
                />
            </Form.Item>
        </Form>
    );
});

PatternWatchlistForm.displayName = 'PatternWatchlistForm';
